import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiSettings, 
  FiPlus, 
  FiChevronDown,
  FiMail,
  FiMessageSquare,
  FiDownload,
  FiX,
  FiCheck,
  FiClock,
  FiPause,
  FiSquare,
  FiAlertTriangle,
  FiFileText,
  FiEye,
  FiFilter,
  FiUser,
  FiCalendar,
  FiTag,
  FiActivity,
  FiPlay,
  FiCheckCircle
} from 'react-icons/fi';

// Import the nursing tasks service
import nursingTasksService from '../../services/nursingTaskService'; // Adjust path as needed
import { API_DOCTOR_LIST } from '../../../../helpers/urlHelper';
// Constants from the model
const TASK_STATUS = {
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'InProgress',
  COMPLETED: 'Completed',
  CANCELED: 'Canceled',
  OVERDUE: 'Overdue'
};

const TASK_PRIORITY = {
  LOW: 'Low',
  NORMAL: 'Normal',
  HIGH: 'High',
  CRITICAL: 'Critical'
};

const CONSTANTS = {
  PRIORITIES: Object.values(TASK_PRIORITY),
  STATUSES: Object.values(TASK_STATUS),
  MAX_TITLE_LENGTH: 200,
  MAX_DETAILS_LENGTH: 1000,
  MAX_INSTRUCTIONS_LENGTH: 500,
  MAX_NOTES_LENGTH: 500
};

// Default form state
const DEFAULT_TASK_STATE = {
  title: '',
  scheduledFor: '',
  status: TASK_STATUS.SCHEDULED,
  priority: TASK_PRIORITY.NORMAL,
  orderedBy: '', // This will store the doctor ID
  details: '',
  instructions: '',
  patientConsent: false,
  notes: '',
  patientId: ''
};

const DEFAULT_FILTERS_STATE = {
  status: 'All',
  priority: 'All',
  orderedBy: '',
  startDate: '',
  endDate: '',
  overdue: false
};

const NursingTreatments = ({ patientId, onCountUpdate, componentName }) => {
  // State management
  const [nursingTasks, setNursingTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState({
    tasks: false,
    submitting: false,
    doctors: false,
    taskDetails: false
  });
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  
  // Offcanvas states
  const [offcanvas, setOffcanvas] = useState({
    add: false,
    view: false,
    filter: false
  });
  
  // Dropdown states
  const [dropdowns, setDropdowns] = useState({
    mostRecent: false
  });
  
  const [selectedTask, setSelectedTask] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS_STATE);
  const [newTask, setNewTask] = useState({
    ...DEFAULT_TASK_STATE,
    patientId: patientId
  });

  // Utility functions
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  }, []);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  }, []);

  const getStatusConfig = useCallback((status) => {
    const configs = {
      [TASK_STATUS.COMPLETED]: { color: 'success', icon: <FiCheck /> },
      [TASK_STATUS.IN_PROGRESS]: { color: 'primary', icon: <FiActivity /> },
      [TASK_STATUS.SCHEDULED]: { color: 'info', icon: <FiClock /> },
      [TASK_STATUS.OVERDUE]: { color: 'danger', icon: <FiAlertTriangle /> },
      [TASK_STATUS.CANCELED]: { color: 'secondary', icon: <FiX /> }
    };
    return configs[status] || { color: 'secondary', icon: <FiSquare /> };
  }, []);

  const getPriorityColor = useCallback((priority) => {
    const colors = {
      [TASK_PRIORITY.CRITICAL]: 'danger',
      [TASK_PRIORITY.HIGH]: 'warning',
      [TASK_PRIORITY.NORMAL]: 'info',
      [TASK_PRIORITY.LOW]: 'secondary'
    };
    return colors[priority] || 'secondary';
  }, []);

  const isTaskOverdue = useCallback((task) => {
    if (task.status === TASK_STATUS.COMPLETED || task.status === TASK_STATUS.CANCELED) {
      return false;
    }
    return new Date(task.scheduledFor) < new Date();
  }, []);

  // Helper function to get doctor name by ID
  const getDoctorNameById = useCallback((doctorId) => {
    const doctor = doctors.find(doc => doc._id === doctorId);
    return doctor ? `${doctor.name} ${doctor.surname}` : doctorId || '-';
  }, [doctors]);

  // API functions
  const fetchDoctors = useCallback(async () => {
    setLoading(prev => ({ ...prev, doctors: true }));
    try {
      const doctorsResponse = await fetch(`${API_DOCTOR_LIST}`);
      if (!doctorsResponse.ok) {
        throw new Error('Failed to fetch doctors');
      }
      const doctorsData = await doctorsResponse.json();
      
      const doctorsArray = doctorsData && doctorsData.data ? doctorsData.data : [];
      setDoctors(doctorsArray);
    } catch (err) {
      console.error('Error fetching doctors:', err);
      showNotification('Error loading doctors', 'error');
      setDoctors([]);
    } finally {
      setLoading(prev => ({ ...prev, doctors: false }));
    }
  }, []);

  // Función principal para cargar datos de nursing tasks
  const loadNursingTasks = useCallback(async () => {
    if (!patientId) {
      console.warn('No patientId provided to NursingTasks');
      setNursingTasks([]);
      return;
    }

    setLoading(prev => ({ ...prev, tasks: true }));
    try {
      const response = await nursingTasksService.getTasksByPatient(patientId);
      const tasksData = response.data || [];
      
      // IMPORTANTE: Solo actualizar si los datos realmente cambiaron
      if (tasksData.length !== nursingTasks.length) {
        setNursingTasks(tasksData);
        // Actualizar contador cuando los datos cambien
        if (onCountUpdate && componentName) {
          onCountUpdate(componentName, tasksData.length);
        }
      }
    } catch (error) {
      console.error('Error loading nursing tasks:', error);
      showNotification(`Could not load nursing tasks: ${error.message}`, 'error');
      setNursingTasks([]);
      // También actualizar el contador en caso de error (0 elementos)
      if (onCountUpdate && componentName) {
        onCountUpdate(componentName, 0);
      }
    } finally {
      setLoading(prev => ({ ...prev, tasks: false }));
    }
  }, [patientId, nursingTasks.length, onCountUpdate, componentName]);

  // Filter functions
  const applyFilters = useCallback(() => {
    let filtered = [...nursingTasks];

    // Global search filter
    if (globalFilter) {
      const searchTerm = globalFilter.toLowerCase();
      filtered = filtered.filter(task => 
        task.title.toLowerCase().includes(searchTerm) ||
        task.details.toLowerCase().includes(searchTerm) ||
        getDoctorNameById(task.orderedBy).toLowerCase().includes(searchTerm)
      );
    }

    // Apply specific filters
    Object.entries(filters).forEach(([key, value]) => {
      if (!value || value === 'All') return;
      
      switch (key) {
        case 'status':
        case 'priority':
        case 'orderedBy':
          filtered = filtered.filter(task => task[key] === value);
          break;
        case 'startDate':
          filtered = filtered.filter(task => {
            const taskDate = new Date(task.scheduledFor);
            return taskDate >= new Date(value);
          });
          break;
        case 'endDate':
          filtered = filtered.filter(task => {
            const taskDate = new Date(task.scheduledFor);
            return taskDate <= new Date(value);
          });
          break;
        case 'overdue':
          if (value) {
            filtered = filtered.filter(task => isTaskOverdue(task));
          }
          break;
      }
    });

    setFilteredTasks(filtered);
  }, [nursingTasks, globalFilter, filters, isTaskOverdue, getDoctorNameById]);

  const getActiveFilters = useCallback(() => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({ key: 'search', label: `Search: "${globalFilter}"` });
    }
    
    const filterLabels = {
      status: 'Status',
      priority: 'Priority',
      orderedBy: 'Ordered By',
      startDate: 'From',
      endDate: 'To',
      overdue: 'Overdue Only'
    };
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'All' && value !== false) {
        const label = filterLabels[key];
        let displayValue;
        
        if (['startDate', 'endDate'].includes(key)) {
          displayValue = new Date(value).toLocaleDateString();
        } else if (key === 'orderedBy') {
          displayValue = getDoctorNameById(value);
        } else {
          displayValue = value === true ? 'Yes' : value;
        }
        
        activeFilters.push({ key, label: `${label}: ${displayValue}` });
      }
    });
    
    return activeFilters;
  }, [globalFilter, filters, getDoctorNameById]);

  // Offcanvas and UI handlers
  const toggleOffcanvas = useCallback((offcanvasName, state = null) => {
    setOffcanvas(prev => ({
      ...prev,
      [offcanvasName]: state !== null ? state : !prev[offcanvasName]
    }));
  }, []);

  const toggleDropdown = useCallback((dropdownName, state = null) => {
    setDropdowns(prev => ({
      ...prev,
      [dropdownName]: state !== null ? state : !prev[dropdownName]
    }));
  }, []);

  const showNotification = useCallback((message, type = 'info') => {
    if (window.Swal) {
      window.Swal.fire({
        icon: type === 'error' ? 'error' : type === 'success' ? 'success' : 'info',
        title: type === 'error' ? 'Error' : type === 'success' ? 'Success' : 'Information',
        text: message,
        timer: type === 'success' ? 3000 : 5000,
        toast: true,
        position: 'top-end',
        showConfirmButton: false
      });
    } else {
      alert(message);
    }
  }, []);

  // Form handlers
  const handleFormChange = useCallback((field, value) => {
    setNewTask(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleFilterChange = useCallback((filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
  }, []);

  const removeFilter = useCallback((filterKey) => {
    if (filterKey === 'search') {
      setGlobalFilter('');
    } else {
      const resetValue = ['status', 'priority'].includes(filterKey) ? 'All' : 
                       filterKey === 'overdue' ? false : '';
      handleFilterChange(filterKey, resetValue);
    }
  }, [handleFilterChange]);

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS_STATE);
    setGlobalFilter('');
  }, []);

  // Task actions - Only view functionality
  const handleViewTask = useCallback(async (taskId) => {
    try {
      setLoading(prev => ({ ...prev, taskDetails: true }));
      
      // Find the task in the current data first
      const existingTask = nursingTasks.find(task => task._id === taskId);
      if (existingTask) {
        setSelectedTask(existingTask);
        toggleOffcanvas('view', true);
        setLoading(prev => ({ ...prev, taskDetails: false }));
        return;
      }

      // If not found locally, try to fetch from API
      const response = await nursingTasksService.getTaskById(taskId);
      setSelectedTask(response.data || response);
      toggleOffcanvas('view', true);
    } catch (error) {
      console.error('Error loading task details:', error);
      // If API fails, still try to show the local task data
      const existingTask = nursingTasks.find(task => task._id === taskId);
      if (existingTask) {
        setSelectedTask(existingTask);
        toggleOffcanvas('view', true);
      } else {
        showNotification(`Error loading task details: ${error.message}`, 'error');
      }
    } finally {
      setLoading(prev => ({ ...prev, taskDetails: false }));
    }
  }, [nursingTasks, toggleOffcanvas, showNotification]);

  const handleAddNew = useCallback(() => {
    setNewTask({ ...DEFAULT_TASK_STATE, patientId });
    toggleOffcanvas('add', true);
  }, [patientId, toggleOffcanvas]);

  const handleSubmitNewTask = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading.submitting) return;
    setLoading(prev => ({ ...prev, submitting: true }));
    
    try {
      // Validation
      const requiredFields = {
        title: 'Title',
        scheduledFor: 'Scheduled Date',
        orderedBy: 'Doctor',
        details: 'Details',
        instructions: 'Instructions',
        patientId: 'Patient ID'
      };

      const missingFields = Object.keys(requiredFields).filter(field => 
        !newTask[field] || newTask[field].toString().trim() === ''
      );

      if (missingFields.length > 0) {
        const missingFieldNames = missingFields.map(field => requiredFields[field]);
        throw new Error(`Please fill in the following required fields: ${missingFieldNames.join(', ')}`);
      }

      // Prepare submission data
      const submissionData = {
        ...newTask,
        title: newTask.title.trim(),
        orderedBy: newTask.orderedBy.trim(),
        details: newTask.details.trim(),
        instructions: newTask.instructions.trim(),
        notes: newTask.notes?.trim() || ''
      };

      const result = await nursingTasksService.createTask(submissionData);
      
      if (result.success !== false) {
        // Recargar datos después de crear un nuevo task
        await loadNursingTasks();
        toggleOffcanvas('add', false);
        showNotification(result.message || 'Nursing task created successfully!', 'success');
      } else {
        throw new Error(result.message || 'Failed to create task');
      }
    } catch (error) {
      console.error('Error creating nursing task:', error);
      showNotification(`Error creating task: ${error.message}`, 'error');
    } finally {
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  }, [loading.submitting, newTask, loadNursingTasks, toggleOffcanvas, showNotification]);

  const handleMostRecentAction = useCallback((action) => {
    toggleDropdown('mostRecent', false);
    switch (action) {
      case 'sendMail':
        console.log('Send mail action');
        break;
      case 'sendMessage':
        console.log('Send message action');
        break;
      case 'downloadReport':
        console.log('Download report action');
        break;
      default:
        break;
    }
  }, [toggleDropdown]);

  // Updated table columns with status column
  const columns = useMemo(() => [
    {
      accessorKey: 'title',
      header: 'Description',
      size: 200,
      Cell: ({ cell, row }) => (
        <div>
          <strong>{cell.getValue()}</strong>
          <div className="text-muted small">
            {row.original.orderedBy && `Ordered by: ${getDoctorNameById(row.original.orderedBy)}`}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'details',
      header: 'Content',
      size: 150,
      Cell: ({ cell }) => {
        const content = cell.getValue();
        if (!content) return '-';
        return content.length > 100 ? `${content.substring(0, 100)}...` : content;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      Cell: ({ cell, row }) => {
        const status = cell.getValue();
        const config = getStatusConfig(status);
        const isOverdue = isTaskOverdue(row.original);
        
        return (
          <div className="d-flex align-items-center gap-1">
            <span className={`badge bg-${config.color} d-flex align-items-center gap-1`}>
              {config.icon}
              <span style={{ fontSize: '0.75em' }}>{status}</span>
            </span>
            {isOverdue && (
              <span className="badge bg-danger" title="Overdue">
                <FiAlertTriangle size={12} />
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'scheduledFor',
      header: 'Date',
      size: 100,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      accessorKey: 'performedBy',
      header: 'Validated By',
      size: 120,
      Cell: ({ cell, row }) => {
        if (row.original.status === TASK_STATUS.COMPLETED && cell.getValue()) {
          return cell.getValue();
        } else if (row.original.orderedBy) {
          return getDoctorNameById(row.original.orderedBy);
        }
        return '-';
      },
    },
    {
      accessorKey: '_id',
      header: 'Action',
      size: 80,
      enableSorting: false,
      Cell: ({ cell, row }) => (
        <button
          className="btn btn-link btn-sm text-primary p-0"
          onClick={(e) => {
            e.stopPropagation();
            handleViewTask(cell.getValue());
          }}
        >
          <FiEye className="me-1" />
          View
        </button>
      ),
    },
  ], [formatDate, handleViewTask, getDoctorNameById, getStatusConfig, isTaskOverdue]);

  // Effects
  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  // Efecto principal para cargar datos cuando cambie el patientId
  useEffect(() => {
    if (patientId) {
      loadNursingTasks();
    }
  }, [patientId, loadNursingTasks]);

  // Efecto separado para actualizar contador cuando cambien los datos
  useEffect(() => {
    if (onCountUpdate && componentName) {
      onCountUpdate(componentName, nursingTasks.length);
    }
  }, [nursingTasks.length, onCountUpdate, componentName]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setDropdowns(prev => ({ ...prev, mostRecent: false }));
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const activeFilters = getActiveFilters();

  // Si está cargando, mostrar spinner
  if (loading.tasks) {
    return (
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Nursing ({nursingTasks.length})</h6>
        </div>
        <div className="card-body">
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading nursing tasks...</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="p-4 bg-white h-100 rounded-10 shadow-sm">

      <h3 className="mb-3" style={{ fontSize: '1.125rem' }}>Nursing</h3>

        {/* Enhanced Search and Actions Bar */}
        <div className="filter-container mb-4">
          <div className="row">

              {/* Search Input */}
              <div className="col-md-5 col-lg-6 d-none d-md-block mb-3 mb-md-0">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                />
              </div>

              <div className="col-md-7 col-lg-6 d-flex justify-content-end gap-1 gap-sm-2 flex-wrap">
                {/* Most Recent Dropdown */}
                <div className="dropdown flex-fill flex-grow-sm-0">
                  <button
                    className="btn btn-outline-secondary d-flex gap-2"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDropdown('mostRecent');
                    }}
                  >
                    Most Recent <FiChevronDown />
                  </button>
                  
                  {dropdowns.mostRecent &&  (
                    <div className="dropdown-menu show" style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      zIndex: 1000,
                      minWidth: '160px'
                    }}>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handleMostRecentAction('sendMail')}
                      >
                        <FiMail /> Send Mail
                      </button>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handleMostRecentAction('sendMessage')}
                      >
                        <FiMessageSquare /> Send Message
                      </button>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handleMostRecentAction('downloadReport')}
                      >
                        <FiDownload /> Download Report
                      </button>
                    </div>
                  )}
                </div>

                {/* Filter Button */}
                <button
                  className="btn btn-outline-secondary position-relative"
                  onClick={() => toggleOffcanvas('filter', true)}
                >
                  <FiFilter />
                  
                  {activeFilters.length > 0 && (
                    <span className="badge bg-primary rounded-pill">
                      {activeFilters.length}
                    </span>
                  )}
                </button>

                {/* Add New Button */}
                <button
                  className="btn btn-primary addnewbtn d-flex gap-2"
                  onClick={handleAddNew}
                >
                  Add
                </button>
              </div>

          </div>
        </div>

        {/* Active Filters Display */}
        {activeFilters.length > 0 && (
          <div className="mb-3">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="text-muted small">Active filters:</span>
              {activeFilters.map((filter, index) => (
                <span
                  key={index}
                  className="badge bg-light text-dark border d-flex align-items-center gap-1"
                >
                  {filter.label}
                  <button
                    type="button"
                    className="btn-close"
                    style={{ fontSize: '0.7em' }}
                    onClick={() => removeFilter(filter.key)}
                  ></button>
                </span>
              ))}
              <button
                className="btn btn-link btn-sm text-decoration-none p-0"
                onClick={handleClearFilters}
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Table */}
     
          <MaterialReactTable
            columns={columns}
            data={filteredTasks}
            enableRowSelection
            onRowSelectionChange={setRowSelection}
            state={{
              rowSelection,
              isLoading: loading.tasks,
              globalFilter
            }}
            enableGlobalFilter={false}
            enableColumnFilters={false}
            enableSorting={true}
            enablePagination={true}
            initialState={{
              pagination: { pageSize: 10 },
              sorting: [{ id: 'scheduledFor', desc: false }]
            }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleViewTask(row.original._id),
              style: { cursor: 'pointer' }
            })}
            renderEmptyRowsFallback={() => (
              <div className="text-center py-4">
                <FiFileText size={48} className="text-muted mb-3" />
                <p className="text-muted">No nursing tasks found</p>
                <button className="btn btn-primary" onClick={handleAddNew}>
                  <FiPlus />
                  Add New Task
                </button>
              </div>
            )}
          />
      

      {/* Add Task Offcanvas */}
      {offcanvas.add && (
        <div className="offcanvas offcanvas-end show" style={{visibility: 'visible'}}>
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Add New Nursing Task</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => toggleOffcanvas('add', false)}
            ></button>
          </div>
          <div className="offcanvas-body">
            <form onSubmit={handleSubmitNewTask}>
              <div className="mb-3">
                <label className="form-label">Title *</label>
                <input
                  type="text"
                  className="form-control"
                  value={newTask.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  maxLength={CONSTANTS.MAX_TITLE_LENGTH}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Scheduled For *</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={newTask.scheduledFor}
                  onChange={(e) => handleFormChange('scheduledFor', e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Priority</label>
                <select
                  className="form-select"
                  value={newTask.priority}
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                >
                  {CONSTANTS.PRIORITIES.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Ordered By (Doctor) *</label>
                <select
                  className="form-select"
                  value={newTask.orderedBy}
                  onChange={(e) => handleFormChange('orderedBy', e.target.value)}
                  required
                >
                  <option value="">Select a doctor</option>
                  {doctors.map(doctor => (
                    <option key={doctor._id} value={doctor._id}>
                      {doctor.name} {doctor.surname}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Details *</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={newTask.details}
                  onChange={(e) => handleFormChange('details', e.target.value)}
                  maxLength={CONSTANTS.MAX_DETAILS_LENGTH}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Instructions *</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={newTask.instructions}
                  onChange={(e) => handleFormChange('instructions', e.target.value)}
                  maxLength={CONSTANTS.MAX_INSTRUCTIONS_LENGTH}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={newTask.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  maxLength={CONSTANTS.MAX_NOTES_LENGTH}
                />
              </div>

              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="patientConsent"
                  checked={newTask.patientConsent}
                  onChange={(e) => handleFormChange('patientConsent', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="patientConsent">
                  Patient Consent Required
                </label>
              </div>

              <div className="d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary flex-fill"
                  disabled={loading.submitting}
                >
                  {loading.submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Creating...
                    </>
                  ) : (
                    'Create Task'
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => toggleOffcanvas('add', false)}
                  disabled={loading.submitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Task Offcanvas - Only Information Display */}
      {offcanvas.view && selectedTask && (
        <div className="offcanvas offcanvas-end show" style={{visibility: 'visible', width: '400px'}}>
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Task Details</h5>
            <button
              type="button"
              className="btn-close"onClick={() => toggleOffcanvas('view', false)}
            ></button>
          </div>
          <div className="offcanvas-body">
            {loading.taskDetails ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading task details...</p>
              </div>
            ) : (
              <div>
                {/* Task Header */}
                <div className="mb-4">
                  <h6 className="fw-bold text-primary">{selectedTask.title}</h6>
                  <div className="d-flex align-items-center gap-2 mt-2">
                    {(() => {
                      const config = getStatusConfig(selectedTask.status);
                      return (
                        <span className={`badge bg-${config.color} d-flex align-items-center gap-1`}>
                          {config.icon}
                          {selectedTask.status}
                        </span>
                      );
                    })()}
                    <span className={`badge bg-${getPriorityColor(selectedTask.priority)}`}>
                      {selectedTask.priority} Priority
                    </span>
                    {isTaskOverdue(selectedTask) && (
                      <span className="badge bg-danger">
                        <FiAlertTriangle className="me-1" />
                        Overdue
                      </span>
                    )}
                  </div>
                </div>

                {/* Task Information */}
                <div className="row g-3">
                  <div className="col-12">
                    <div className="border rounded p-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FiCalendar className="text-muted" />
                        <strong>Scheduled For</strong>
                      </div>
                      <p className="mb-0">{formatDateTime(selectedTask.scheduledFor)}</p>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="border rounded p-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FiUser className="text-muted" />
                        <strong>Ordered By</strong>
                      </div>
                      <p className="mb-0">{getDoctorNameById(selectedTask.orderedBy)}</p>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="border rounded p-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FiFileText className="text-muted" />
                        <strong>Details</strong>
                      </div>
                      <p className="mb-0">{selectedTask.details || 'No details provided'}</p>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="border rounded p-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FiActivity className="text-muted" />
                        <strong>Instructions</strong>
                      </div>
                      <p className="mb-0">{selectedTask.instructions || 'No instructions provided'}</p>
                    </div>
                  </div>

                  {selectedTask.notes && (
                    <div className="col-12">
                      <div className="border rounded p-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <FiTag className="text-muted" />
                          <strong>Notes</strong>
                        </div>
                        <p className="mb-0">{selectedTask.notes}</p>
                      </div>
                    </div>
                  )}

                  <div className="col-12">
                    <div className="border rounded p-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FiCheckCircle className="text-muted" />
                        <strong>Patient Consent</strong>
                      </div>
                      <p className="mb-0">
                        {selectedTask.patientConsent ? (
                          <span className="text-success">
                            <FiCheck className="me-1" />
                            Required
                          </span>
                        ) : (
                          <span className="text-muted">
                            <FiX className="me-1" />
                            Not Required
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {selectedTask.performedBy && selectedTask.status === TASK_STATUS.COMPLETED && (
                    <div className="col-12">
                      <div className="border rounded p-3 bg-light">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <FiCheckCircle className="text-success" />
                          <strong>Completed By</strong>
                        </div>
                        <p className="mb-1">{selectedTask.performedBy}</p>
                        {selectedTask.completedAt && (
                          <small className="text-muted">
                            Completed on: {formatDateTime(selectedTask.completedAt)}
                          </small>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="col-12">
                    <div className="border rounded p-3 bg-light">
                      <div className="row g-2">
                        <div className="col-6">
                          <small className="text-muted d-block">Created</small>
                          <small>{formatDateTime(selectedTask.createdAt)}</small>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block">Updated</small>
                          <small>{formatDateTime(selectedTask.updatedAt)}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Offcanvas */}
      {offcanvas.filter && (
        <div className="offcanvas offcanvas-end show" style={{visibility: 'visible'}}>
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Filter Tasks</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => toggleOffcanvas('filter', false)}
            ></button>
          </div>
          <div className="offcanvas-body">
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="All">All Statuses</option>
                {CONSTANTS.STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Priority</label>
              <select
                className="form-select"
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="All">All Priorities</option>
                {CONSTANTS.PRIORITIES.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Ordered By</label>
              <select
                className="form-select"
                value={filters.orderedBy}
                onChange={(e) => handleFilterChange('orderedBy', e.target.value)}
              >
                <option value="">All Doctors</option>
                {doctors.map(doctor => (
                  <option key={doctor._id} value={doctor._id}>
                    {doctor.name} {doctor.surname}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>

            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="overdueFilter"
                checked={filters.overdue}
                onChange={(e) => handleFilterChange('overdue', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="overdueFilter">
                Show overdue tasks only
              </label>
            </div>

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary flex-fill"
                onClick={() => toggleOffcanvas('filter', false)}
              >
                Apply Filters
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClearFilters}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop for offcanvas */}
      {(offcanvas.add || offcanvas.view || offcanvas.filter) && (
        <div
          className="offcanvas-backdrop fade show"
          onClick={() => {
            setOffcanvas({ add: false, view: false, filter: false });
          }}
        ></div>
      )}
    </div>
  );
};

export default NursingTreatments;